import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { 
  Check, 
  MapPin, 
  Utensils, 
  Home, 
  Users, 
  Heart,
  Shield,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const Experiences = () => {
  useEffect(() => {
    document.title = 'Our Experiences - Wild Thyme Women\'s Wellbeing Retreats';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Discover our nourishing women\'s retreats. Weekend and midweek options in beautiful UK locations. All meals, accommodation, and gentle activities included.';
  }, []);

  return (
    <div className="min-h-screen pt-32" data-testid="experiences-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F9F8F6]" data-testid="experiences-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Join Us
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-[#2C332B] mb-8">
              Our Experiences
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D]">
              Take the first step toward reconnection, rest, and renewal
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 md:py-32" data-testid="overview-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C332B] mb-6">
              A Nourishing Experience for Women
            </h2>
            <div className="space-y-4 text-lg leading-relaxed text-[#6C757D]">
              <p>
                Our retreats are thoughtfully designed spaces for women seeking time away from
                everyday demands-a chance to slow down, reconnect with yourself, and enjoy
                meaningful moments with others who understand.
              </p>
              <p>
                Each retreat combines home-cooked nourishing food, gentle time in nature,
                reflective activities, and the warmth of supportive company-all hosted in
                beautiful, changing locations across the UK.
              </p>
              <p>
                You don't need to be outdoorsy or experienced. You just need to be ready for
                something different-something restorative.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-[#F9F8F6]" data-testid="whats-included-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              What's Included
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B]">
              Everything You Need
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Home,
                title: 'Comfortable Accommodation',
                items: [
                  'Private or shared room options',
                  'Clean, welcoming spaces',
                  'Beautiful rural or coastal settings',
                  'All bedding and towels provided',
                ],
              },
              {
                icon: Utensils,
                title: 'Nourishing Food',
                items: [
                  'Home-cooked meals using local ingredients',
                  'Vegetarian and dietary needs catered for',
                  'Breakfast, lunch, dinner, and snacks',
                  'Herbal teas and fresh water always available',
                ],
              },
              {
                icon: MapPin,
                title: 'Activities',
                items: [
                  'Set your own limits',
                  'Guidance & support',
                  'Optional journaling and quiet time',
                  'No pressure, no performance',
                ],
              },
              {
                icon: Users,
                title: 'Group Connection',
                items: [
                  'Small groups (typically 8-12 women)',
                  'Warm, facilitated conversations',
                  'Space to share or simply listen',
                  'A sense of being truly understood',
                ],
              },
            ].map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white rounded-3xl p-8"
                data-testid={`included-${idx}`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#3A4A3F] rounded-xl flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#2C332B]">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {section.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-[#6C757D]">
                      <Check className="w-5 h-5 text-[#D4A373] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Retreat Is For */}
      <section className="py-24 md:py-32" data-testid="who-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Is This for You?
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C332B] mb-8">
              Who This Retreat Is For
            </h2>
            <div className="space-y-6 text-lg leading-relaxed text-[#6C757D]">
              <p>
                This retreat is for women just like you.. who feel ready for a pause-whether you're managing
                stress, going through transition, feeling burnt out, or simply craving meaningful
                time away.
              </p>
              <p>
                You might be a busy professional, a parent needing space, someone navigating change,
                or simply curious about what time in nature and good company might offer.
              </p>
              <p>
                You don't need to be outdoorsy, spiritual, or experienced in retreats. You just need
                to be open to slowing down, being yourself, and allowing space for rest and
                reflection.
              </p>
            </div>

            <div className="mt-12 bg-[#F9F8F6] rounded-3xl p-8">
              <h3 className="font-serif text-xl font-semibold text-[#2C332B] mb-4">
                You'll feel at home here if you:
              </h3>
              <ul className="space-y-3">
                {[
                  'Value authenticity and genuine connection',
                  'Appreciate nourishing food and gentle rhythms',
                  'Want to step away from screens and to-do lists',
                  'Feel ready to prioritize yourself for a few days',
                  'Are open to meeting other thoughtful women',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[#6C757D]">
                    <Heart className="w-5 h-5 text-[#D4A373] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Beach Image Section (replaces Typical Day) */}
      <section className="py-24 md:py-32 bg-[#E8E4DD]" data-testid="beach-image-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/qx94dc31_PXL_20250817_181008111.jpg"
              alt="Peaceful beach at sunset"
              className="w-full max-w-4xl h-auto rounded-3xl shadow-lg object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Safety & Wellbeing */}
      <section className="py-24 md:py-32" data-testid="safety-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
                Feeling Safe Matters
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#2C332B] mb-4">
                How We Look After You
              </h2>
              <p className="text-lg text-[#6C757D]">
                Your comfort, safety, and wellbeing are at the heart of everything we do
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <Shield className="w-8 h-8 text-[#D4A373] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-[#2C332B] mb-3">
                      Getting to Know You
                    </h3>
                    <p className="text-[#6C757D] leading-relaxed mb-4">
                      After you book, we'll send you a simple, friendly form asking about things like dietary preferences, any health considerations we should know about, and what kind of support would help you feel most at ease.
                    </p>
                    <p className="text-[#6C757D] leading-relaxed">
                      This isn't a medical questionnaire - it's just us getting to know you so we can create the most nourishing experience possible. Everything you share stays private and is treated with respect and care.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <Heart className="w-8 h-8 text-[#D4A373] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-[#2C332B] mb-3">
                      While You're With Us
                    </h3>
                    <p className="text-[#6C757D] leading-relaxed mb-4">
                      Our retreat hosts are warm, experienced people who genuinely care about your wellbeing. They're skilled at creating a safe, supportive atmosphere where everyone can relax and be themselves.
                    </p>
                    <p className="text-[#6C757D] leading-relaxed">
                      Every activity is optional and offered with gentleness - there's absolutely no expectation to participate in anything that doesn't feel right for you. Need some quiet time? Extra reassurance? A chat? We're here, and we understand.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F9F8F6] rounded-3xl p-8">
                <h3 className="font-serif text-xl font-semibold text-[#2C332B] mb-3">
                  Making Space for Everyone
                </h3>
                <p className="text-[#6C757D] leading-relaxed">
                  We're committed to welcoming women with different needs and circumstances. If you have mobility considerations, sensory sensitivities, dietary requirements, or anything else that would help us support you better, please reach out before booking. We'll have a genuine conversation about what's possible and work together to make sure the retreat feels accessible and comfortable for you.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Past Guests Say */}
      <section className="py-24 md:py-32 bg-[#3A4A3F] text-white" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-white/60 mb-4">
              Testimonials
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-4">
              What Past Guests Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                quote:
                  'Such a beautiful balance of activity and calm. The walking, swimming and quiet moments together made the whole weekend feel incredibly special.',
                author: 'Becky',
              },
              {
                quote:
                  'It was the perfect mix of adventure and comfort. Time outdoors, meaningful conversations and space to simply breathe.',
                author: 'Nicola',
              },
              {
                quote:
                  'The setting, the atmosphere and the people made it unforgettable. I came away feeling lighter, calmer and genuinely inspired.',
                author: 'Julie',
              },
              {
                quote:
                  'I loved how relaxed everything felt. Nothing rushed, just great company, amazing food and the chance to really switch off.',
                author: 'Sheila',
              },
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 flex flex-col h-full"
                data-testid={`testimonial-${idx}`}
              >
                <div className="mb-4">
                  <svg
                    className="w-8 h-8 text-[#D4A373] opacity-60"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="text-white/95 leading-relaxed mb-4 italic flex-grow">
                  "{testimonial.quote}"
                </p>
                <p className="text-sm font-medium text-white/70 mt-auto">— {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B] mb-6">
              Ready to Join Us?
            </h2>
            <p className="text-lg text-[#6C757D] mb-8">
              Take the first step toward meaningful time away
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 px-10 py-5 text-lg font-medium bg-[#3A4A3F] text-white rounded-full hover:bg-[#2C332B] transition-all duration-300 hover:scale-[1.02]"
              data-testid="book-cta"
            >
              Book Your Retreat
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
